/* global PiniaSetupStoreHelpers */
import type { UnwrapRef } from 'vue';
import type { Writable, WritableDeep } from 'type-fest';
import type { RequiredKeys } from '@/utils/types';
import type { NewAirRoadViewDataComposable } from '@/composables/data/useNewAirRoadViewData';
import type * as openapi from '@/helpers/api/types/openapiOverrides';
import type { ARServiceItem } from '@/models/airRoad';

import { computed, reactive, ref, toRefs, watch } from 'vue';
import { useRoute } from 'vue-router';
import { defineStore } from 'pinia';
import _cloneDeep from 'lodash/cloneDeep';
import _isEqual from 'lodash/isEqual';
// import _omit from 'lodash/omit';

import * as airRoadHelpers from '@/helpers/airRoad';
import * as airRoadConstants from '@/constants/airRoad';
import * as routeNames from '@/constants/routeNames';

interface DefPrivateNewAirRoadViewState {
  data: RequiredKeys<
    Partial<Writable<UnwrapRef<NewAirRoadViewDataComposable>>>
  >;
}

interface DefNewAirRoadViewState {
  stepNum: number;
  bookingStep: airRoadConstants.BookingStep | null;
  bookingState: {
    params: openapi.AirroadSingleCreateQueryOpenapi;
    data: openapi.AirroadSingleCreateBodyOpenapi;
    _extraProducts: openapi.AirroadJobDetailedContentOpenapi['stops'][0]['pickup_products'];
    _deliverySpecialOptions: Record<string, ARServiceItem>;
    _pickupSpecialOptions: Record<string, ARServiceItem>;
  };
  bookingStepValidationState: {
    LEG_CHOOSE: boolean | null;
    PACKAGE: boolean | null;
    SPECIAL_OPTIONS: boolean | null;
    CONFIRM: boolean | null;
  };
}

const stepCount = airRoadConstants.bookingSteps.length;

const defPrivateNewAirRoadViewState: DefPrivateNewAirRoadViewState = {
  data: {
    isFetchingAirRoadBookingJob: undefined,
    isFetchingAddresses: undefined,
    isFetchingAirRoadJobCreateData: undefined,
    airRoadBookingJob: undefined,
    addresses: undefined,
    airRoadJobCreateData: undefined,
    airRoadBookingJobError: undefined,
    addressesError: undefined,
    airRoadJobCreateDataError: undefined,
    refreshAirRoadBookingJob: undefined,
    refreshAddresses: undefined,
    refreshAirRoadJobCreateData: undefined,
    reload: undefined,
  },
};

const defNewAirRoadViewState: DefNewAirRoadViewState = {
  stepNum: 1,
  bookingStep: airRoadConstants.bookingStepValues.LEG_CHOOSE,
  bookingState: _cloneDeep(
    airRoadConstants.defaultBookingState,
  ) as WritableDeep<typeof airRoadConstants.defaultBookingState>,
  bookingStepValidationState: _cloneDeep(
    airRoadConstants.defaultBookingStepValidationState,
  ) as WritableDeep<typeof airRoadConstants.defaultBookingStepValidationState>,
};

function privateNewAirRoadViewStoreSetup(helpers: PiniaSetupStoreHelpers) {
  const state = reactive(
    _cloneDeep(defPrivateNewAirRoadViewState),
  ) as DefPrivateNewAirRoadViewState;
  const route = useRoute();

  async function $reset(): Promise<void> {
    const reload = state.data.reload;
    Object.assign(state, _cloneDeep(defPrivateNewAirRoadViewState));
    if (route.name === routeNames.AIR_ROAD_JOBS_NEW) {
      if (reload) {
        setData(reload()); // reload data while on the page
      }
    }
  }

  function setData(viewData: NewAirRoadViewDataComposable) {
    state.data = reactive(viewData);
  }

  watch(
    () => route.name,
    function () {
      if (route.name !== routeNames.AIR_ROAD_JOBS_NEW) {
        void $reset(); // reset when leave the page
      }
    },
    { immediate: true },
  );

  return {
    ...toRefs(state),
    $reset,
    setData,
  };
}

function newAirRoadViewStoreSetup(helpers: PiniaSetupStoreHelpers) {
  const state = reactive(_cloneDeep(defNewAirRoadViewState));

  // https://pinia.vuejs.org/cookbook/composables.html#Setup-Stores
  const _legChooseFormRef = ref<HTMLFormElement | null>(null);
  const _packageFormRef = ref<HTMLFormElement | null>(null);
  const _specialOptionsFormRef = ref<HTMLFormElement | null>(null);
  const _confirmFormRef = ref<HTMLFormElement | null>(null);

  const _newAirRoadViewStore = usePrivateNewAirRoadViewStore();

  const jobId = computed(function () {
    return _newAirRoadViewStore.data.airRoadBookingJob?.rockhopper_id || NaN;
  });
  const jobDate = computed(function () {
    return _newAirRoadViewStore.data.airRoadBookingJob?.rockhopper_date || '';
  });

  const excludedLegs = computed(function () {
    return _newAirRoadViewStore.data.airRoadBookingJob?.exclude_legs || [];
  });

  const isAirRoadJobExists = computed(function () {
    if (!_newAirRoadViewStore.data.airRoadBookingJob) return false;
    const stopCount = _newAirRoadViewStore.data.airRoadBookingJob.stops.length;
    if (stopCount === 2) {
      if (excludedLegs.value.includes(1)) return true;
    } else if (stopCount === 3) {
      if (excludedLegs.value.includes(1) && excludedLegs.value.includes(2)) {
        return true;
      }
    }
    return false;
  });

  const data = computed(function () {
    return _newAirRoadViewStore.data;
  });

  const packageFormFirstErrorMessage = computed(function () {
    const errors = _packageFormRef.value?.errors;
    if (errors?.[0]?.errorMessages[0]) {
      return errors[0].errorMessages[0] as string;
    }
    return '';
  });

  async function $reset(): Promise<void> {
    Object.assign(state, _cloneDeep(defNewAirRoadViewState));
    _newAirRoadViewStore.$reset();
  }

  function init(viewData: NewAirRoadViewDataComposable) {
    _newAirRoadViewStore.setData(viewData);
  }

  function setLegChooseFormElement(element: HTMLFormElement | null): void {
    _legChooseFormRef.value = element;
  }

  function setPackageFormElement(element: HTMLFormElement | null): void {
    _packageFormRef.value = element;
  }

  function setSpecialOptionsFormElement(element: HTMLFormElement | null): void {
    _specialOptionsFormRef.value = element;
  }

  function setConfirmFormElement(element: HTMLFormElement | null): void {
    _confirmFormRef.value = element;
  }

  function handlePrevStepClick(): void {
    if (_newAirRoadViewStore.data.isFetchingAirRoadJobCreateData) return;
    if (state.bookingStep === airRoadConstants.bookingStepValues.CONFIRM) {
      state.bookingStep = airRoadConstants.bookingStepValues.SPECIAL_OPTIONS;
    } else if (
      state.bookingStep === airRoadConstants.bookingStepValues.SPECIAL_OPTIONS
    ) {
      state.bookingStep = airRoadConstants.bookingStepValues.PACKAGE;
    } else if (
      state.bookingStep === airRoadConstants.bookingStepValues.PACKAGE
    ) {
      state.bookingStep = airRoadConstants.bookingStepValues.LEG_CHOOSE;
    }
  }

  async function handleNextStepClick(): Promise<void> {
    if (_newAirRoadViewStore.data.isFetchingAirRoadJobCreateData) return;
    if (state.bookingStep === airRoadConstants.bookingStepValues.LEG_CHOOSE) {
      if (!_legChooseFormRef.value) return;
      const { valid } = await _legChooseFormRef.value.validate();
      if (!valid) return;
      state.bookingStep = airRoadConstants.bookingStepValues.PACKAGE;
    } else if (
      state.bookingStep === airRoadConstants.bookingStepValues.PACKAGE
    ) {
      if (!_packageFormRef.value) return;
      const { valid } = await _packageFormRef.value.validate();
      if (!valid) return;
      state.bookingStep = airRoadConstants.bookingStepValues.SPECIAL_OPTIONS;
    } else if (
      state.bookingStep === airRoadConstants.bookingStepValues.SPECIAL_OPTIONS
    ) {
      if (!_legChooseFormRef.value) return;
      const { valid } = await _legChooseFormRef.value.validate();
      if (!valid) return;
      state.bookingStep = airRoadConstants.bookingStepValues.CONFIRM;
    } else if (
      state.bookingStep === airRoadConstants.bookingStepValues.CONFIRM
    ) {
      if (!_confirmFormRef.value) return;
      const { valid } = await _confirmFormRef.value.validate();
      if (!valid) return;
      if (_newAirRoadViewStore.data.refreshAirRoadJobCreateData) {
        await _newAirRoadViewStore.data.refreshAirRoadJobCreateData(
          state.bookingState,
        );
      }
      state.bookingStep = null;
    }
  }

  watch(
    () => state.stepNum,
    (newValue, oldValue) => {
      /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
      const newStepNum: any = newValue;
      state.stepNum = parseInt(newStepNum);
      if (!(state.stepNum >= 1 && state.stepNum <= stepCount)) {
        state.stepNum = 1;
      }
    },
  );

  watch(
    () => _newAirRoadViewStore.data.airRoadBookingJob,
    function (newValues, oldValues) {
      if (_isEqual(newValues, oldValues)) return;
      if (!_newAirRoadViewStore.data.airRoadBookingJob) return;
      // make defaults and do reset if data was updated somehow
      state.bookingStep = airRoadConstants.bookingStepValues.LEG_CHOOSE;
      Object.assign(
        state.bookingState,
        _cloneDeep(airRoadConstants.defaultBookingState),
      );
      state.bookingState.data.job_id =
        _newAirRoadViewStore.data.airRoadBookingJob.rockhopper_id;
      state.bookingState.data.job_date =
        _newAirRoadViewStore.data.airRoadBookingJob.rockhopper_date;
      const stopCount =
        _newAirRoadViewStore.data.airRoadBookingJob.stops.length;
      if (stopCount === 3 && excludedLegs.value.includes(1)) {
        state.bookingState.data.ar_booked_leg = 2;
      }
      state.bookingState.data.ar_stops =
        _newAirRoadViewStore.data.airRoadBookingJob.stops.map(
          airRoadHelpers.getAirRoadStopItemValue,
        );
    },
    { immediate: true },
  );

  // since sometimes `bookingState` model is changed without fields interaction, it needs to watch it and run manual form validate
  // TODO maybe watch that specific values because normal fields becomes red without interaction
  watch(
    () => state.bookingState,
    function (newValues, oldValues) {
      if (state.bookingStep === airRoadConstants.bookingStepValues.LEG_CHOOSE) {
        if (_legChooseFormRef.value) _legChooseFormRef.value.validate();
      } else if (
        state.bookingStep === airRoadConstants.bookingStepValues.PACKAGE
      ) {
        if (_packageFormRef.value) _packageFormRef.value.validate();
      } else if (
        state.bookingStep === airRoadConstants.bookingStepValues.SPECIAL_OPTIONS
      ) {
        if (_specialOptionsFormRef.value) {
          _specialOptionsFormRef.value.validate();
        }
      } else if (
        state.bookingStep === airRoadConstants.bookingStepValues.CONFIRM
      ) {
        if (_confirmFormRef.value) _confirmFormRef.value.validate();
      }
    },
    {
      deep: true,
    },
  );

  // reset model `bookingState.data.items` if `ar_booked_leg` is changed
  // TODO maybe we need to cancel added extra products too
  watch(
    () => state.bookingState.data.ar_booked_leg,
    function (newValues, oldValues) {
      if (_isEqual(newValues, oldValues)) return;
      state.bookingState.data.items = [];
    },
  );

  return {
    ...toRefs(state),
    jobId,
    jobDate,
    excludedLegs,
    isAirRoadJobExists,
    data,
    packageFormFirstErrorMessage,
    $reset,
    init,
    setLegChooseFormElement,
    setPackageFormElement,
    setSpecialOptionsFormElement,
    setConfirmFormElement,
    handlePrevStepClick,
    handleNextStepClick,
  };
}

const usePrivateNewAirRoadViewStore = defineStore(
  '_newAirRoadView',
  privateNewAirRoadViewStoreSetup,
);

export const useNewAirRoadViewStore = defineStore(
  'newAirRoadView',
  newAirRoadViewStoreSetup,
);
